import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { EventClickArg } from '@fullcalendar/core';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  SnackbarCloseReason,
  GlobalStyles,
  useTheme,
} from '@mui/material';
import useGetEventsByRange from '@src/hooks/api/queries/modes/useGetEventsByRange';
import moment from 'moment-timezone';
import EventPopover from './EventPopover';
import EventEditorWindow from './EventEditorWindow';
import { CalendarEvent } from '@src/types/Events';
import { UUID } from 'crypto';
import useGetStacks from '@src/hooks/api/queries/modes/useGetStacks';
import { Stack as FracStack } from '@src/types/Stacks';
import CalendarHeader from './CalendarHeader';
import EventTile from './EventTile';
import EventDeleteDialog from './EventDeleteDialog';
import storeConnector from '@src/store/storeConnector';
import { ConfigState } from '@src/store/reducers/configData';
import { ServiceState } from '@src/store/reducers/serviceData';
import { getTsDueTimeMode } from '@src/utils/index_ts';
import {
  calendarContainer,
  calendarPaper,
  calendarContent,
  getFullCalendarGlobalStyles,
} from './styles';
import { applyEventStyling } from '@components/Home/Config/Calendar/utils';

function CalendarView({
  siteMeta,
  timeMode,
}: {
  siteMeta: ConfigState['siteMeta'];
  timeMode: ServiceState['timeMode'];
}) {
  const theme = useTheme();
  const calendarRef = useRef<FullCalendar>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]);
  const [range, setRange] = useState<{
    from: string | null;
    to: string | null;
  }>({
    from: null,
    to: null,
  });

  const { data: events } = useGetEventsByRange({
    body: {
      from: range?.from,
      to: range?.to,
    },
  });

  useEffect(() => {
    if (events && Array.isArray(events)) {
      setAllEvents(events);
    }
  }, [events]);

  const calendarEvents = useMemo(() => {
    return allEvents.map((event) => {
      const { rrule, ...rest } = event;
      return rrule ? { ...rest, rrule } : rest;
    });
  }, [allEvents]);

  const currentTZ =
    getTsDueTimeMode(siteMeta, timeMode, moment().toISOString()).tz() || 'UTC';

  const {
    data: stacksListData,
    isFetching: isStackFetching,
    isRefetching: isStackRefetching,
  } = useGetStacks();

  const stacksList = stacksListData || ([] as FracStack[]);

  const getStackName = useCallback(
    (stackId: UUID) => {
      const stack = stacksList.find((s) => s.id === stackId);
      return stack ? stack.name : 'Unknown Stack';
    },
    [stacksList],
  );

  const handleDateClick = (dateClickInfo: DateClickArg) => {
    const startDate = moment.tz(dateClickInfo.dateStr, currentTZ);
    const endDate = moment
      .tz(dateClickInfo.dateStr, currentTZ)
      .add(0.5, 'hour');

    setActiveEvent({
      title: '',
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      duration: '00:30:00',
      backgroundColor: '#5FC8F1',
      stackId: undefined,
      startCommands: [],
      stopCommands: [],
    });
    setDialogOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const event = clickInfo.event;
    const foundEvent = allEvents?.find((e) => e.id === event.id);
    const start = event.start;
    const rrule = event._def.recurringDef?.typeData.rruleSet
      ? foundEvent?.rrule
      : undefined;

    if (!foundEvent) {
      console.error('Event not found or missing start date');
      return;
    }

    const [hours, minutes] = foundEvent.duration.split(':').map(Number);
    const eventStart = moment(start).tz(currentTZ).toISOString();
    const eventEnd = moment(start)
      .tz(currentTZ)
      .add(hours, 'hours')
      .add(minutes, 'minutes')
      .toISOString();

    setActiveEvent({
      id: event.id as UUID,
      title: event.title,
      start: eventStart,
      end: eventEnd,
      ...(rrule
        ? {
            rrule: rrule,
          }
        : {}),
      duration: foundEvent.duration,
      backgroundColor: event.backgroundColor || '',
      stackId: event.extendedProps.stackId || undefined,
      startCommands: event.extendedProps.startCommands || [],
      stopCommands: event.extendedProps.stopCommands || [],
    });
    setAnchorEl(clickInfo.el);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleEditEvent = () => {
    setDialogOpen(true);
    handlePopoverClose();
  };

  const handleAddEvent = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setActiveEvent(null);
  };

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setConfirmOpen(false);
  };

  const handleDatesSet = useCallback((dateInfo: { start: Date; end: Date }) => {
    const newFrom = moment(dateInfo.start).toISOString();
    const newTo = moment(dateInfo.end).toISOString();

    setRange({ from: newFrom, to: newTo });
  }, []);

  if (isStackFetching || isStackRefetching) {
    return <CircularProgress />;
  }

  return (
    <Box sx={calendarContainer}>
      <GlobalStyles styles={getFullCalendarGlobalStyles(theme)} />
      <Snackbar
        open={confirmOpen}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={error ? 'error' : 'success'}
          variant='filled'
        >
          {error ? error : 'Event updated successfully!'}
        </Alert>
      </Snackbar>
      <Paper elevation={0} sx={calendarPaper}>
        <CalendarHeader onAddEvent={handleAddEvent} calendarRef={calendarRef} />

        <Box sx={calendarContent}>
          <FullCalendar
            nowIndicator={true}
            now={new Date()}
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              momentTimezonePlugin,
              rrulePlugin,
            ]}
            initialView='timeGridWeek'
            events={calendarEvents}
            headerToolbar={{
              left: '',
              center: '',
              right: '',
            }}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            timeZone={currentTZ}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
              hour12: true,
            }}
            height='100%'
            themeSystem='standard'
            eventContent={EventTile}
            eventDidMount={applyEventStyling}
          />
        </Box>
      </Paper>

      <EventPopover
        anchorEl={anchorEl}
        onEdit={handleEditEvent}
        onClose={handlePopoverClose}
        activeEvent={activeEvent}
        setOpen={setDeleteDialogOpen}
        stackName={getStackName(activeEvent?.stackId as UUID)}
        currentTZ={currentTZ}
      />
      <EventEditorWindow
        dialogOpen={dialogOpen}
        activeEvent={activeEvent as CalendarEvent}
        stacksList={stacksList}
        onClose={handleDialogClose}
        setError={setError}
        setDialogOpen={setDialogOpen}
        setConfirmOpen={setConfirmOpen}
      />

      {deleteDialogOpen && activeEvent?.id && (
        <EventDeleteDialog
          setOpen={setDeleteDialogOpen}
          eventId={activeEvent?.id}
          setAnchorEl={setAnchorEl}
        />
      )}
    </Box>
  );
}

export default storeConnector(CalendarView, {
  user: ['role'],
  config: ['siteMeta'],
  service: ['timeMode'],
});
